import React, { ReactNode } from 'react';

import { Description, Field, Input as HeadlessInput, Label } from '@headlessui/react';

import cn from '~/utils/cn';

export interface InputProps {
  /**
   * Whether or not to disable the input.
   */
  disabled?: boolean;
  /**
   * Whether or not the input has a validation error.
   */
  invalid?: boolean;
  /**
   * The name to use when submitting an HTML form.
   */
  name?: string;
  /**
   * The initial value for the input.
   */
  defaultValue?: string;
  /**
   * The controlled value of the input.
   */
  value?: string;
  /**
   * Handler to call when the input value changes.
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Additional CSS classes to apply to the input.
   */
  className?: string;
  /**
   * The type of input (text, password, email, etc.)
   */
  type?: string;
  /**
   * Placeholder text for the input
   */
  placeholder?: string;
  /**
   * Label text for the input
   */
  label?: string;
  /**
   * Description text for the input
   */
  description?: string;
  /**
   * Error message to display when invalid is true
   */
  errorMessage?: string;
  /**
   * Icon component to display alongside the input
   */
  icon?: ReactNode;
  /**
   * Position of the icon (left or right)
   */
  iconPosition?: 'left' | 'right';
}

const Input: React.FC<InputProps> = ({
  disabled = false,
  invalid = false,
  name,
  defaultValue,
  value,
  onChange,
  className = '',
  type = 'text',
  placeholder,
  label,
  description,
  errorMessage,
  icon,
  iconPosition = 'left',
}) => {
  const hasIcon = !!icon;
  const iconClasses = iconPosition === 'left' ? 'pl-10' : 'pr-10';

  return (
    <Field disabled={disabled} className="w-full">
      {label && <Label className="text-grey-700 mb-8 block text-sm font-medium">{label}</Label>}

      <div className="relative">
        {hasIcon && iconPosition === 'left' && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">{icon}</div>
        )}

        <HeadlessInput
          name={name}
          defaultValue={defaultValue}
          value={value}
          onChange={onChange}
          type={type}
          placeholder={placeholder}
          className={cn(
            'border-grey-300 block w-full rounded-md bg-white p-12 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm',
            {
              'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500': invalid,
              'cursor-not-allowed opacity-50': disabled,
              [iconClasses]: hasIcon,
            },
            className
          )}
        />

        {hasIcon && iconPosition === 'right' && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">{icon}</div>
        )}
      </div>

      {description && !invalid && <Description className="text-grey-500 mt-12 text-sm">{description}</Description>}

      {invalid && errorMessage && <Description className="mt-12 text-sm text-red-600">{errorMessage}</Description>}
    </Field>
  );
};

export default Input;
